// One collector's day (or week / month): every call, SMS, email, promise and note, newest first, each linked to the account.
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Handshake, Loader2, Mail, MessageSquare, PhoneIncoming, PhoneMissed, PhoneOutgoing, StickyNote } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { ageingBasePath } from "@/lib/regionFmt";
import { getCollectorTimeline } from "@/lib/api";

const ICON = {
  call: (i) => (i.direction === "inbound" ? <PhoneIncoming className="w-3.5 h-3.5 text-sky-600" /> : i.answered ? <PhoneOutgoing className="w-3.5 h-3.5 text-emerald-600" /> : <PhoneMissed className="w-3.5 h-3.5 text-slate-400" />),
  sms: () => <MessageSquare className="w-3.5 h-3.5 text-violet-600" />,
  email: () => <Mail className="w-3.5 h-3.5 text-amber-600" />,
  p2p: (i) => <Handshake className={`w-3.5 h-3.5 ${i.status === "kept" ? "text-emerald-600" : i.status === "broken" ? "text-rose-600" : "text-indigo-600"}`} />,
  note: () => <StickyNote className="w-3.5 h-3.5 text-slate-500" />,
};
const FILTERS = [["all", "All"], ["call", "Calls"], ["sms", "SMS"], ["email", "Emails"], ["p2p", "Promises"], ["note", "Notes"]];
const PERIOD_LABEL = { today: "today", yesterday: "yesterday", week: "this week", month: "this month" };

export function CollectorTimeline({ collector, period, onBack, backLabel = "Scoreboard" }) {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [filter, setFilter] = useState("all");
  useEffect(() => {
    setData(null);
    getCollectorTimeline(collector, period).then(setData).catch((e) => toast.error("Timeline: " + (e?.response?.data?.detail || e.message)));
  }, [collector, period]);

  const fmt = (iso) => new Date(iso).toLocaleString("en-GB", { timeZone: data?.tz, weekday: period === "today" || period === "yesterday" ? undefined : "short", day: period === "today" ? undefined : "2-digit", month: period === "today" ? undefined : "short", hour: "2-digit", minute: "2-digit" });
  const items = (data?.items || []).filter((i) => filter === "all" || i.kind === filter);

  return (
    <div className="space-y-2" data-testid="collector-timeline">
      <div className="flex flex-wrap items-center gap-2">
        <Button variant="ghost" size="sm" className="h-7 text-xs" onClick={onBack} data-testid="timeline-back"><ArrowLeft className="w-3.5 h-3.5 mr-1" />{backLabel}</Button>
        <p className="text-sm font-semibold" data-testid="timeline-title">{collector} <span className="font-normal text-slate-500">· {PERIOD_LABEL[period]}</span></p>
        {data && <span className="text-[11px] text-slate-500" data-testid="timeline-counts">{data.counts.call} calls · {data.counts.sms} SMS · {data.counts.email} emails · {data.counts.p2p} promises · {data.counts.note} notes · {data.files_touched} {data.files_touched === 1 ? "file" : "files"} touched</span>}
        <div className="inline-flex rounded-md border bg-white p-0.5 text-[11px] ml-auto">
          {FILTERS.map(([k, l]) => <button key={k} type="button" onClick={() => setFilter(k)} className={`px-2 py-0.5 rounded ${filter === k ? "bg-slate-900 text-white" : "text-slate-600 hover:bg-slate-100"}`} data-testid={`timeline-filter-${k}`}>{l}</button>)}
        </div>
      </div>
      {!data ? <div className="py-10 text-center"><Loader2 className="w-5 h-5 animate-spin inline text-slate-400" /></div> : (
        <div className="overflow-auto max-h-[60vh] border rounded-md divide-y" data-testid="timeline-list">
          {items.length === 0 && <p className="px-3 py-6 text-center text-xs text-slate-400">Nothing logged for {collector} {PERIOD_LABEL[period]}.</p>}
          {items.map((i, idx) => (
            <div key={idx} className="px-3 py-1.5 flex items-start gap-2 text-xs hover:bg-[#f7f9fc]" data-testid={`timeline-item-${i.kind}`}>
              <span className="mt-0.5 shrink-0">{(ICON[i.kind] || ICON.note)(i)}</span>
              <span className={`${period === "today" ? "w-[48px]" : "w-[124px]"} shrink-0 tabular-nums text-slate-500 whitespace-nowrap`}>{fmt(i.at)}</span>
              <span className="min-w-0 flex-1 truncate text-slate-800" title={i.summary}>{i.summary}</span>
              {i.case_id ? (
                <button type="button" onClick={() => navigate(`${ageingBasePath()}/${i.case_id}`)} className="shrink-0 max-w-[220px] truncate text-sky-700 hover:underline underline-offset-2" title={`${i.customer_name || ""} ${i.sf || ""}`} data-testid={`timeline-case-${i.case_id}`}>
                  {i.customer_name || i.sf || i.case_id}
                </button>
              ) : <span className="shrink-0 text-slate-400">{i.who || i.phone || ""}</span>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
